import { ArrowLeft } from "lucide-react";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-gray-50 border-b-2 border-gray-100 py-8">
        <div className="max-w-4xl mx-auto px-6">
          <a
            href="/"
            data-testid="privacy-back-link"
            className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[#0A0A0A] font-bold uppercase tracking-wider mb-6 transition-colors"
            style={{ fontFamily: "'Satoshi', sans-serif" }}
          >
            <ArrowLeft size={16} strokeWidth={3} />
            Back to Home
          </a>
          <h1
            className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-tight text-[#0A0A0A]"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            data-testid="privacy-heading"
          >
            Privacy Policy
          </h1>
          <p className="text-gray-500 mt-3 font-medium" style={{ fontFamily: "'Satoshi', sans-serif" }}>
            Last updated: December 2025
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-16 space-y-10" style={{ fontFamily: "'Satoshi', sans-serif" }}>
        <Section title="1. Introduction">
          VelTech Ghana ("we", "us", "our") operates a cloud-based school management system. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform and services. We are committed to protecting the privacy of all users including school administrators, teachers, students, parents, and guardians.
        </Section>

        <Section title="2. Information We Collect">
          <p className="mb-3">We collect the following types of information:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Personal Information:</strong> Names, email addresses, phone numbers, physical addresses of school staff, students, parents, and guardians.</li>
            <li><strong>Student Records:</strong> Academic records, attendance data, exam results, report cards, health information, and behavioral records as entered by authorized school personnel.</li>
            <li><strong>Financial Data:</strong> Fee payment records, invoices, expense records, and payroll information managed through our platform.</li>
            <li><strong>Usage Data:</strong> Login times, features accessed, browser type, IP address, and device information for system performance and security purposes.</li>
            <li><strong>Communication Data:</strong> Messages sent through the platform between teachers, parents, and administrators.</li>
          </ul>
        </Section>

        <Section title="3. How We Use Your Information">
          <ul className="list-disc pl-6 space-y-2">
            <li>To provide, operate, and maintain our school management system.</li>
            <li>To manage student enrollment, attendance, academic records, and reporting.</li>
            <li>To facilitate communication between schools, teachers, parents, and students.</li>
            <li>To process fee payments and manage school financial records.</li>
            <li>To generate reports and analytics for school administration.</li>
            <li>To provide customer support and respond to inquiries.</li>
            <li>To improve our platform and develop new features.</li>
            <li>To comply with legal obligations and Ghana Education Service (GES) requirements.</li>
          </ul>
        </Section>

        <Section title="4. Data Storage and Security">
          All data is stored on secure cloud servers with bank-grade encryption (AES-256). We implement daily automated backups, role-based access controls, and regular security audits. Access to student data is strictly limited to authorized school personnel as configured by the school administrator. We do not sell, trade, or rent personal information to third parties.
        </Section>

        <Section title="5. Data Sharing">
          <p className="mb-3">We only share data in the following circumstances:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>With authorized school personnel who require access to perform their duties.</li>
            <li>With parents/guardians regarding their own child's information through the parent portal.</li>
            <li>When required by law, regulation, or legal process in Ghana.</li>
            <li>To comply with Ghana Education Service (GES) reporting requirements as directed by the school.</li>
            <li>With service providers who assist us in operating our platform, subject to strict confidentiality agreements.</li>
          </ul>
        </Section>

        <Section title="6. Children's Privacy">
          Our platform stores information about students, including minors. This information is provided and managed by authorized school personnel and is accessible to parents/guardians through the parent portal. We do not knowingly collect personal information directly from children. All student data is managed under the authority and responsibility of the subscribing school.
        </Section>

        <Section title="7. Data Retention">
          We retain school data for as long as the school's subscription is active. Upon cancellation, we provide a full data export and retain data for 90 days before permanent deletion. Schools may request earlier deletion by contacting us in writing.
        </Section>

        <Section title="8. Your Rights">
          <p className="mb-3">Under Ghana's Data Protection Act, 2012 (Act 843), you have the right to:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Access your personal data held by us.</li>
            <li>Request correction of inaccurate personal data.</li>
            <li>Request deletion of your personal data (subject to legal obligations).</li>
            <li>Object to processing of your personal data.</li>
            <li>Request a copy of your data in a portable format.</li>
          </ul>
        </Section>

        <Section title="9. Compliance">
          VelTech Ghana operates in compliance with Ghana's Data Protection Act, 2012 (Act 843) and relevant regulations issued by the Data Protection Commission of Ghana. We are committed to ensuring that all data processing activities meet the requirements set forth in this legislation.
        </Section>

        <Section title="10. Contact Us">
          <p>If you have questions about this Privacy Policy or wish to exercise your data rights, contact us at:</p>
          <div className="mt-4 p-6 bg-gray-50 border-2 border-gray-100">
            <p><strong>VelTech Ghana</strong></p>
            <p>Email: info@veltechgh.com</p>
            <p>Phone: 0531 988 775</p>
            <p>WhatsApp: 0505 066 691</p>
            <p>Location: Accra, Ghana</p>
          </div>
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2
        className="text-xl font-black text-[#0A0A0A] mb-4 uppercase tracking-tight"
        style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
      >
        {title}
      </h2>
      <div className="text-gray-600 text-sm leading-relaxed font-medium" style={{ fontFamily: "'Satoshi', sans-serif" }}>
        {children}
      </div>
    </div>
  );
}
